import * as moment from 'moment';

import { isScheduleEvent } from './schedule-events';

/**
 * Slot capacity rule shared by every booking path. `Schedule.patientsLimit` is 'capped' or
 * 'unlimited'; rows created before the setting existed hold null, which we read as the
 * default new groups get (capped, 1 per slot) so a clash still warns.
 */
export const DEFAULT_PATIENTS_PER_TIME_SLOT = 1;

export function effectivePatientsLimit(schedule: any): 'capped' | 'unlimited' {
  return schedule?.patientsLimit === 'unlimited' ? 'unlimited' : 'capped';
}

export function effectivePatientsPerTimeSlot(schedule: any): number {
  const n = Number(schedule?.patientsPerTimeSlot);
  return Number.isFinite(n) && n >= 1 ? Math.floor(n) : DEFAULT_PATIENTS_PER_TIME_SLOT;
}

/** True when `conflictCount` existing bookings already fill the slot. */
export function isSlotFull(conflictCount: number, schedule: any): boolean {
  if (effectivePatientsLimit(schedule) === 'unlimited') return false;
  return conflictCount >= effectivePatientsPerTimeSlot(schedule);
}

/**
 * Whether booking into a slot should ask first, given what the availability check found there.
 * Any event in the slot asks (a busy one fills it, one that is not busy is still worth knowing
 * about); bookings ask once they reach the practice's capacity.
 */
export function shouldWarnOfConflict(conflicts: any[], schedule: any): boolean {
  const rows = conflicts || [];
  if (rows.some(isScheduleEvent)) return true;
  return isSlotFull(rows.length, schedule);
}

export type AppointmentStatusAction =
  | 'arrived' | 'not_arrived' | 'cancelled' | 'not_cancelled' | 'did_not_attend' | 'did_attend'
  | 'confirmed' | 'not_confirmed' | 'completed';

const UNCONFIRMED = { confirmedAt: null, confirmedById: null, confirmedByName: null };

/** The status fields an appointment ends up with after a status-menu action. Does not mutate. */
export function applyAppointmentStatus(appointment: any, status: AppointmentStatusAction | string): any {
  const next = { ...appointment };
  switch (status) {
    case 'arrived':
      next.patientArrivedTime = moment().toISOString();
      next.isCancelled = false;
      next.didNotAttend = false;
      break;
    case 'not_arrived':
      next.patientArrivedTime = null;
      next.isCancelled = false;
      next.didNotAttend = false;
      break;
    case 'cancelled':
      next.isCancelled = true;
      next.patientArrivedTime = null;
      next.didNotAttend = false;
      Object.assign(next, UNCONFIRMED);
      break;
    case 'not_cancelled':
      next.isCancelled = false;
      break;
    case 'did_not_attend':
      next.didNotAttend = true;
      next.patientArrivedTime = null;
      next.isCancelled = false;
      break;
    case 'did_attend':
      next.didNotAttend = false;
      break;
    // Shown at once; the server stamps the real time and who (appointment-confirmation.ts).
    case 'confirmed':
      next.confirmedAt = next.confirmedAt || moment().toISOString();
      break;
    case 'not_confirmed':
      Object.assign(next, UNCONFIRMED);
      break;
    // The visit is done (the front desk loads the recording for the doctor): note saved, ended unless it already is.
    case 'completed':
      next.noteSaved = true;
      next.endTime = next.endTime || moment().toISOString();
      next.isCancelled = false;
      next.didNotAttend = false;
      break;
  }
  return next;
}

/**
 * Cancelled and did-not-attend bookings do not hold their slot, so moving one back to an
 * active state can double-book it and needs the same conflict check as a new booking.
 */
export function reoccupiesSlot(before: any, after: any): boolean {
  const freed = (a: any) => !!a?.isCancelled || !!a?.didNotAttend;
  return freed(before) && !freed(after);
}
